import { useEffect, useRef } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Icon,
  Button,
  Flex,
  Spacer,
  Text,
  Card,
  Field,
  withErrorBoundary,
} from '@oliasoft-open-source/react-ui-library';
import emptyIcon from 'src/assets/empty.svg';
import translations from 'src/internationalisation/translation-map.json';
import { getResolver } from 'src/validation/resolver';
import { compareSchemaValidator } from 'schemas/ajv-validators.ts';
import { Input, RadioButton } from 'client/common/form-inputs';
import { debounce } from 'lodash';
import { useAutoSave } from 'src/common/auto-save/use-auto-save';
import { autoSaveWait } from 'src/config/config';
import { updateCompare } from 'store/entities/compare-estimates/compare-estimates';
import {
  ICompare,
  ICompareActiveEstimate,
  ICompareEstimates,
  IEstimateItem,
} from 'src/common/interfaces/compare-estimates.interfaces';
import { selectCompanyNameById } from 'store/entities/companies/selectors';
import { selectSubjectId } from 'store/entities/authorization/selectors';
import type { TRootState } from 'src/store/store-types';
import { CompareEstimatesInputTable } from './compare-estimates-input-table';

interface ICompareEstimatesInputTab extends PropsFromRedux {
  companyName: string;
  currentUserId: string;
  visibleEstimates: ICompareEstimates[];
  compare: ICompare;
  showCompareModalUpdated: (value: boolean) => void;
  filteredEstimates: IEstimateItem[];
  activeEstimates: ICompareActiveEstimate[];
  setActiveTabIndex: (value: number) => void;
}

const CompareEstimatesInputTab = ({
  companyName,
  currentUserId,
  visibleEstimates,
  compare,
  updateCompare,
  showCompareModalUpdated,
  filteredEstimates,
  activeEstimates,
  setActiveTabIndex,
}: ICompareEstimatesInputTab) => {
  const { compareId, name, subjectId, designs } = compare;
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      compareId: compareId || null,
      name: name || '',
      designs,
      isPublic: compare.isPublic,
    },
    resolver: getResolver(compareSchemaValidator),
  });

  useEffect(() => {
    reset({
      compareId: compare.compareId,
      name: compare.name,
      designs: compare.designs,
      isPublic: compare.isPublic,
    });
  }, [compare.compareId, compare.name, compare.designs, compare.isPublic]);

  const debounceUpdateCompare = useRef(debounce(updateCompare, autoSaveWait));

  const onSubmit = handleSubmit((data) => {
    debounceUpdateCompare.current(data);
  });

  useAutoSave(onSubmit, watch);
  const showTable = visibleEstimates?.length > 0 || compare.designs?.length > 0;

  return (
    <>
      <Spacer />
      <Flex>
        <Field label={t(translations.compare_comparisonName)}>
          <Input
            testId="comparisons-input-comparison-name"
            name="name"
            control={control}
            errors={errors}
            width={300}
            placeholder={t(translations.compare_comparisonName)}
          />
        </Field>
        <Spacer width={16} />
        <Field label={t(translations.projects_accessControl)}>
          <RadioButton
            name="isPublic"
            control={control}
            disabled={subjectId !== currentUserId}
            inline={true}
            options={[
              {
                label: t(translations.projects_onlyMe),
                value: 'false',
              },
              {
                label: companyName,
                value: 'true',
              },
            ]}
          />
        </Field>
      </Flex>
      <Spacer />
      {!showTable && (
        <Card bordered={true}>
          <Flex alignItems="center" justifyContent="center" direction="column" height="25vh">
            <Icon icon={emptyIcon} />
            <Spacer height="var(--padding-xs)" />
            <Text faint>
              {t(translations.compare_noEstimatesHaveBeenAddedYet, {
                value: 'Items',
              })}
            </Text>
            <Spacer height="var(--padding-xs)" />
            <Button
              name="addEstimates"
              label={t(translations.compare_addEstimates, {
                value: 'Items',
              })}
              onClick={() => showCompareModalUpdated(true)}
              colored
              disabled={false}
              testId="comparisons-button-add-estimates"
            />
          </Flex>
        </Card>
      )}
      {showTable && (
        <CompareEstimatesInputTable
          filteredEstimates={filteredEstimates}
          activeCompare={compare}
          activeEstimates={activeEstimates}
          setActiveTabIndex={setActiveTabIndex}
        />
      )}
    </>
  );
};

const mapStateToProps = ({ entities }: TRootState, { companyId }: { companyId: string }) => ({
  companyName: selectCompanyNameById(entities, companyId),
  currentUserId: selectSubjectId(entities),
});

const mapDispatchToProps = { updateCompare };

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = withErrorBoundary(connector(CompareEstimatesInputTab));

export { Container as CompareEstimatesInputTab };
